import Cookies from 'universal-cookie'
import { PackageName } from '~/store/package'

/**
 * BIF-9542 — "Appointment [Beta]" entry point in the Non Qontak One app
 * switcher. Port of BIF-9357 (hub-chat, nuxt3); QI-217 is the canonical spec
 * for the entitlement model.
 *
 * Unlike CRM / Admin console / Insight, this entry does not bounce through the
 * SSO authorization endpoint. Appointment sits under the same parent domain as
 * Hub (`HOST_URL`, `.qontak.com` in production), so it already sees the session
 * cookies written by `setGlobalToken` in `utils/auth.js`. The switch only
 * checks that the shared session exists and then navigates straight to the
 * Appointment origin; the Appointment app does its own exchange or refresh.
 */

/** Session cookies shared across `*.qontak.*` apps — see `utils/auth.js`. */
export const GLOBAL_SSO_TOKEN_COOKIE = 'global_sso_token'
export const GLOBAL_SSO_REFRESH_TOKEN_COOKIE = 'global_sso_refresh_token'

/** Qontak Appointment Module — the component code that entitles the entry. */
export const APPOINTMENT_PACKAGE_CODE = PackageName['CP-QONTAKCHAT-2026-0032']

const cookie = new Cookies()

/**
 * Origin without a trailing slash, so appended paths cannot double up.
 * Empty when `APPOINTMENT_URL` is not configured for the environment.
 */
export function getAppointmentUrl() {
  return String(process.env.APPOINTMENT_URL || '').replace(/\/+$/, '')
}

/**
 * Both cookies are required: Appointment needs the access token to call the API
 * and the refresh token to survive its rotation, and a half-written pair means
 * the token sync has not completed.
 */
export function hasGlobalSsoSession() {
  return (
    !!cookie.get(GLOBAL_SSO_TOKEN_COOKIE) &&
    !!cookie.get(GLOBAL_SSO_REFRESH_TOKEN_COOKIE)
  )
}

/**
 * All five gates must pass:
 *   1. `appConfig.is_appointment_enabled` — app-level kill switch, so the entry
 *      point can be pulled for every account without a deploy.
 *   2. A configured `APPOINTMENT_URL`, so the item never renders as a dead
 *      click on an environment where Appointment is not deployed.
 *   3. Both global SSO cookies present — without the shared session Appointment
 *      would land the user on its own login wall.
 *   4. Not the unified app — the Qontak One navbar is QI-212's surface.
 *   5. `CP-QONTAKCHAT-2026-0032` active. `isEnableBillingPackage` already
 *      returns false for every code when `is_use_package_code` is false, so no
 *      open-by-default fallback is applied here.
 *
 * Hiding the item is a UI convenience; the Appointment app rejects non-entitled
 * accounts server-side.
 */
export function isAppointmentEntryVisible({
  appConfig,
  isUnifiedApp,
  isEnableBillingPackage,
}) {
  return (
    !!appConfig?.is_appointment_enabled &&
    !!getAppointmentUrl() &&
    hasGlobalSsoSession() &&
    !isUnifiedApp &&
    !!isEnableBillingPackage(APPOINTMENT_PACKAGE_CODE)
  )
}
